import { CompositionKind } from 'types/composition';

/**
 * The full kind vocabulary the containment lattice reasons over. `CompositionKind` (from
 * `types/composition`) covers only composition-authored kinds (`surface` | `section` |
 * `fragment`); a code-backed catalog entry with `role: 'primitive'` resolves to `'primitive'`
 * here, and a code-backed entry with `role: 'surface' | 'section'` resolves to that role
 * directly (e.g. `SpaceGrid` is a catalog entry that *is* the Space's surface).
 */
export type LatticeKind = CompositionKind | 'primitive';

/**
 * `space ⊃ surface ⊃ section ⊃ fragment ⊃ primitive`. A host may directly contain only the
 * kinds listed for it — never its own kind or an ancestor kind (`surface ⊃ surface`,
 * `section ⊃ surface`, `fragment ⊃ section` are all forbidden). `'space'` here means "whatever
 * is a direct child of the Space's surface component", not `a2ui.root` itself — see
 * `checkSpaceRootKind` for the root's own constraint.
 */
const ALLOWED_CHILDREN: Record<LatticeKind, readonly LatticeKind[]> = {
  surface: ['section', 'fragment', 'primitive'],
  section: ['fragment', 'primitive'],
  fragment: ['fragment', 'primitive'],
  // A primitive's children are governed by its own catalog `ChildList` props, not the lattice;
  // a composition ref nested inside one is checked against the nearest enclosing composition's
  // kind, so primitives are lattice-transparent for fragment/primitive descendants.
  primitive: ['fragment', 'primitive'],
};

export interface ContainmentCheckResult {
  allowed: boolean;
  reason?: string;
}

/**
 * `ALLOWED_CHILDREN` flattened into one `{ host, child }` pair per legal edge — the single source
 * of truth the corpus coverage gate (`corpus/coverage.ts`) walks to require a golden fixture per
 * lattice-legal placement, so a new edge added to `ALLOWED_CHILDREN` is a gate failure (an
 * uncovered placement) rather than a silent gap.
 */
export const LEGAL_CONTAINMENT_PAIRS: ReadonlyArray<{ host: LatticeKind; child: LatticeKind }> = (
  Object.entries(ALLOWED_CHILDREN) as Array<[LatticeKind, readonly LatticeKind[]]>
).flatMap(([host, children]) => children.map((child) => ({ host, child })));

/**
 * `checkContainment(hostKind, childKind)` — the L2 containment-lattice rule. `hostKind` is the
 * kind of the composition (or catalog surface/section entry) directly hosting `childKind`.
 */
export function checkContainment(hostKind: LatticeKind, childKind: LatticeKind): ContainmentCheckResult {
  const allowedChildren = ALLOWED_CHILDREN[hostKind];
  if (allowedChildren?.includes(childKind)) {
    return { allowed: true };
  }
  return {
    allowed: false,
    reason: `A "${hostKind}" may not directly contain a "${childKind}" (containment lattice: space ⊃ surface ⊃ section ⊃ fragment ⊃ primitive)`,
  };
}

/**
 * A Space document's `a2ui.root` must resolve to a `'surface'`-kind component — the surface
 * hosts sections (uniform layout) or content (primary layout) as ITS children; a `section` or
 * `fragment` placed directly as the document root has no surface to be contained by and is
 * explicitly forbidden by the spec regardless of what its own children are.
 */
export function checkSpaceRootKind(rootKind: LatticeKind): ContainmentCheckResult {
  if (rootKind === 'surface') {
    return { allowed: true };
  }
  return {
    allowed: false,
    reason: `A Space document's a2ui.root must resolve to a "surface"-kind component (received "${rootKind}")`,
  };
}
